import { memo, ReactNode } from 'react';
import { Box, Divider, Paper, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { InvestmentDTO } from '@inversor-hub/types';
import { formatDate, formatPercentage } from '@/utils/misc';
import { getInvestmentCountryLabel } from '@/features/investments/countries';
import { cardSurface } from '../../shared/surfaces';

interface PropertyDetailsProps {
  investment: InvestmentDTO;
}

const Row = ({ label, help, value }: { label: string; help?: string; value: ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 2,
      py: 1,
      borderBottom: '1px solid',
      borderColor: 'grey.100',
      '&:last-of-type': { borderBottom: 0, pb: 0 },
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
      {help ? (
        <Tooltip title={help} arrow>
          <InfoOutlinedIcon
            fontSize="inherit"
            sx={{ ml: 0.5, color: 'text.secondary', cursor: 'help', verticalAlign: 'middle' }}
          />
        </Tooltip>
      ) : null}
    </Typography>
    <Typography variant="body2" fontWeight={600} textAlign="right">
      {value}
    </Typography>
  </Box>
);

export const PropertyDetails = memo(({ investment }: PropertyDetailsProps) => (
  <Paper elevation={0} sx={{ p: 3, height: '100%', ...cardSurface }}>
    <Typography variant="h6" fontWeight={600}>
      El inmueble
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Datos de la propiedad y de tu titularidad.
    </Typography>
    <Divider sx={{ my: 2 }} />

    <Stack>
      <Row label="Dirección" value={investment.address} />
      <Row
        label="Localidad"
        value={`${investment.city}, ${investment.province} · ${investment.postalCode}`}
      />
      {investment.country && investment.country !== 'ES' ? (
        <Row label="País" value={getInvestmentCountryLabel(investment.country)} />
      ) : null}
      <Row
        label="Fecha de escritura"
        value={formatDate(investment.purchaseDate ?? investment.createdAt)}
      />
      <Row
        label="Titularidad"
        help="Porcentaje de propiedad que corresponde a tu participación en esta inversión."
        value={formatPercentage(investment.ownershipPercentage)}
      />
    </Stack>
  </Paper>
));

PropertyDetails.displayName = 'PropertyDetails';
